/**
 * Returns true if two ISO date strings fall on different calendar days.
 */
export function isDifferentDay(a: string, b: string): boolean {
	const dateA = new Date(a);
	const dateB = new Date(b);
	return (
		dateA.getFullYear() !== dateB.getFullYear() ||
		dateA.getMonth() !== dateB.getMonth() ||
		dateA.getDate() !== dateB.getDate()
	);
}

// Helper function to format time from hour/minute to string format
export function formatTimeString(hour: number, minute: number): string {
	return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:00`;
}

// Helper function to parse time string (e.g., "09:00:00") into hour/minute object
export function parseTimeString(timeString: string): { hour: number; minute: number } | null {
	if (!timeString) return null;

	const parts = timeString.split(':');
	if (parts.length < 2) return null;

	const hour = parseInt(parts[0], 10);
	const minute = parseInt(parts[1], 10);

	if (isNaN(hour) || isNaN(minute)) return null;

	return { hour, minute };
}

// Helper function to calculate days elapsed since a given timestamp
export function getDaysElapsed(timestamp: number): number {
	return Math.floor(
		(new Date().getTime() - new Date(timestamp).getTime()) / (1000 * 60 * 60 * 24),
	);
}

/**
 * Formats a Unix timestamp (ms) to ISO string
 * Returns empty string if timestamp is missing or invalid
 */
export function formatTimestampToISO(timestamp: number | undefined): string {
	if (!timestamp || timestamp <= 0) {
		return '';
	}
	return new Date(timestamp).toISOString();
}

/**
 * Formats a date string (ISO) for relative date display
 * Shows "Today at", "Yesterday at", day of week, or full date format
 */
export function formatRelativeDate(
	dateString: string | undefined,
	options: { includeTime?: boolean } = {},
): string {
	const { includeTime = true } = options;

	if (!dateString) {
		return '';
	}

	const date = new Date(dateString);
	if (isNaN(date.getTime())) {
		return '';
	}

	const now = new Date();
	const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
	const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());

	const diffTime = today.getTime() - dateOnly.getTime();
	const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

	const timeString = includeTime
		? date.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
		: '';

	if (diffDays === 0) {
		return includeTime ? `Today at ${timeString}` : 'Today';
	} else if (diffDays === 1) {
		return includeTime ? `Yesterday at ${timeString}` : 'Yesterday';
	} else if (diffDays >= 2 && diffDays <= 7) {
		const dayOfWeek = date.toLocaleString('en-US', { weekday: 'long' });
		return includeTime ? `${dayOfWeek} at ${timeString}` : dayOfWeek;
	} else {
		if (includeTime) {
			return date.toLocaleString('en-US', {
				month: 'short',
				day: 'numeric',
				year: 'numeric',
				hour: 'numeric',
				minute: '2-digit',
				hour12: true,
			});
		}
		return date.toLocaleDateString('en-US', {
			month: 'short',
			day: 'numeric',
			year: 'numeric',
		});
	}
}

/**
 * Formats an ISO date string as a compact relative time label (e.g. "Just now", "5m", "3h", "2d", "Mar 4")
 */
export function formatCompactRelativeTime(isoString: string): string {
	const now = Date.now();
	const then = new Date(isoString).getTime();
	const diffMs = now - then;
	const diffMin = Math.floor(diffMs / 60_000);
	const diffHrs = Math.floor(diffMs / 3_600_000);
	const diffDays = Math.floor(diffMs / 86_400_000);

	if (diffMin < 1) return 'Just now';
	if (diffMin < 60) return `${diffMin}m`;
	if (diffHrs < 24) return `${diffHrs}h`;
	if (diffDays < 7) return `${diffDays}d`;
	return new Date(isoString).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Formats an ISO date string for compact message display (e.g. "Mar 4, 3:45 pm")
 * Omits year and uses lowercase am/pm
 */
export function formatMessageTimestamp(isoString: string): string {
	const date = new Date(isoString);

	return date
		.toLocaleDateString('en-US', {
			month: 'short',
			day: 'numeric',
			hour: 'numeric',
			minute: '2-digit',
			hour12: true,
		})
		.replace(/\s?(AM|PM)/i, (_, p) => ` ${p.toLowerCase()}`);
}

/**
 * Formats a date string (ISO) for standard date display
 * Returns formatted locale string or empty string
 */
export function formatDate(dateString: string | undefined): string {
	if (!dateString) {
		return '';
	}

	const date = new Date(dateString);
	if (isNaN(date.getTime())) {
		return '';
	}

	return date.toLocaleString('en-US', {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
		hour: 'numeric',
		minute: '2-digit',
		hour12: true,
	});
}

/**
 * Formats a Unix timestamp (ms) to a date-only string
 * Returns formatted date string (e.g., "Jan 15, 2024") or undefined if invalid
 */
export function formatTimestampToDate(timestamp?: number | null): string | undefined {
	if (!timestamp || timestamp <= 0) {
		return undefined;
	}

	const date = new Date(timestamp);
	if (isNaN(date.getTime())) {
		return undefined;
	}

	return date.toLocaleDateString('en-US', {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
	});
}

/**
 * Formats a Unix timestamp (ms) to a time-only string
 * Returns formatted time string (e.g., "12:00 AM") or undefined if invalid
 */
export function formatTimestampToTime(timestamp?: number | null): string | undefined {
	if (!timestamp || timestamp <= 0) {
		return undefined;
	}

	const date = new Date(timestamp);
	if (isNaN(date.getTime())) {
		return undefined;
	}

	return date.toLocaleTimeString('en-US', {
		hour: 'numeric',
		minute: '2-digit',
		hour12: true,
	});
}

/**
 * Formats a Unix timestamp (ms) to a date and time string
 * Returns formatted date with time string (e.g., "Jan 15, 2024, 3:30 PM") or undefined if invalid
 */
export function formatTimestampToDateTime(timestamp?: number | null): string | undefined {
	if (!timestamp || timestamp <= 0) {
		return undefined;
	}

	const date = new Date(timestamp);
	if (isNaN(date.getTime())) {
		return undefined;
	}

	return date.toLocaleString('en-US', {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
		hour: 'numeric',
		minute: '2-digit',
		hour12: true,
	});
}

/**
 * Get the start of today (00:00:00) as a Unix timestamp in milliseconds
 */
export function getStartOfToday(): number {
	const now = new Date();
	now.setHours(0, 0, 0, 0);
	return now.getTime();
}

/**
 * Get the end of today (23:59:59.999) as a Unix timestamp in milliseconds
 */
export function getEndOfToday(): number {
	const now = new Date();
	now.setHours(23, 59, 59, 999);
	return now.getTime();
}

/**
 * Get the end of a given day (23:59:59.999) as a Unix timestamp in milliseconds
 */
export function getEndOfDay(timestamp: number): number {
	const date = new Date(timestamp);
	date.setHours(23, 59, 59, 999);
	return date.getTime();
}

/**
 * Get the start of a specific number of days ago
 * Returns Unix timestamp in milliseconds
 */
export function getStartOfDaysAgo(days: number): number {
	const date = new Date();
	date.setDate(date.getDate() - days);
	date.setHours(0, 0, 0, 0);
	return date.getTime();
}

/**
 * Get the start of a specific number of days from now
 * Returns Unix timestamp in milliseconds
 */
export function getStartOfDaysFromNow(days: number): number {
	const date = new Date();
	date.setDate(date.getDate() + days);
	date.setHours(0, 0, 0, 0);
	return date.getTime();
}

/**
 * Get the end of a specific number of days from now
 * Returns Unix timestamp in milliseconds
 */
export function getEndOfDaysFromNow(days: number): number {
	const date = new Date();
	date.setDate(date.getDate() + days);
	date.setHours(23, 59, 59, 999);
	return date.getTime();
}

/**
 * Get the start of the current week (Sunday 00:00:00)
 * Returns Unix timestamp in milliseconds
 */
export function getStartOfThisWeek(): number {
	const now = new Date();
	const day = now.getDay();
	const diff = now.getDate() - day; // Get Sunday
	const sunday = new Date(now.setDate(diff));
	sunday.setHours(0, 0, 0, 0);
	return sunday.getTime();
}

/**
 * Get the end of the current week (Saturday 23:59:59.999)
 * Returns Unix timestamp in milliseconds
 */
export function getEndOfThisWeek(): number {
	const now = new Date();
	const day = now.getDay();
	const diff = now.getDate() + (6 - day); // Get Saturday
	const saturday = new Date(now.setDate(diff));
	saturday.setHours(23, 59, 59, 999);
	return saturday.getTime();
}

/**
 * Get the start of the current month (1st day, 00:00:00)
 * Returns Unix timestamp in milliseconds
 */
export function getStartOfThisMonth(): number {
	const now = new Date();
	const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
	firstDay.setHours(0, 0, 0, 0);
	return firstDay.getTime();
}

/**
 * Get the end of the current month (last day, 23:59:59.999)
 * Returns Unix timestamp in milliseconds
 */
export function getEndOfThisMonth(): number {
	const now = new Date();
	const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
	lastDay.setHours(23, 59, 59, 999);
	return lastDay.getTime();
}

/**
 * Get the start of next month (1st day, 00:00:00)
 * Returns Unix timestamp in milliseconds
 */
export function getStartOfNextMonth(): number {
	const now = new Date();
	const firstDay = new Date(now.getFullYear(), now.getMonth() + 1, 1);
	firstDay.setHours(0, 0, 0, 0);
	return firstDay.getTime();
}

// ============================================================================
// DateInput Component Utilities
// ============================================================================

import { parseDate } from '@internationalized/date';

import type { DateValue } from '@internationalized/date';

/**
 * Converts a Unix timestamp (milliseconds) to a DateValue for use with DateInput component
 *
 * @param timestamp - Unix timestamp in milliseconds, or null/undefined
 * @returns A DateValue object for the DateInput component, or null if the value is invalid
 */
export function timestampToDateValue(timestamp?: number | null): DateValue | null {
	if (!timestamp || timestamp <= 0) {
		return null;
	}

	try {
		const date = new Date(timestamp);
		const isoDateString = date.toISOString().split('T')[0];
		return parseDate(isoDateString);
	} catch (error) {
		console.error('timestampToDateValue: Error converting timestamp', error);
		return null;
	}
}

/**
 * Converts a DateValue from the DateInput component to a Unix timestamp in milliseconds
 *
 * @param dateValue - The DateValue object from the DateInput component
 * @returns Unix timestamp in milliseconds, or undefined if the value is null
 */
export function dateValueToTimestamp(dateValue: DateValue | null): number | undefined {
	if (!dateValue) {
		return undefined;
	}

	return new Date(dateValue.year, dateValue.month - 1, dateValue.day).getTime();
}
